import { ApolloClient, InMemoryCache, gql } from "@apollo/client";
import { ProductClass } from "../graphQL/types/shared";
import { IRootState } from "../rootState";
import getYearlyDataCache, {
  IBaseState,
} from "../sharedComponents/yearlyDataCache";
import { baseURL, IPYDatum } from "../Utils";

export type IState = IBaseState<IPYDatum>;

interface IHashInput {
  productClass: ProductClass;
}

interface IAPIResponse {
  data: IPYDatum[];
}

const getDataFromAPIResponse = ({ data }: IAPIResponse) => data;

export const defaultHashFunction = ({ productClass }: IHashInput) =>
  productClass;

const FETCH_BEGIN = "PRODUCT_YEAR_FETCH_BEGIN";
const FETCH_SUCCESS = "PRODUCT_YEAR_FETCH_SUCCESS";
const FETCH_FAIL = "PRODUCT_YEAR_FETCH_FAIL";
const FETCH_IF_NEEDED = "PRODUCT_YEAR_FETCH_IF_NEEDED";

const GET_PRODUCT_YEAR_DATA = gql`
  query GetProductYearData($productLevel: Int!, $productClass: ProductClass!) {
    productYear(productLevel: $productLevel, productClass: $productClass) {
      productId
      productLevel
      year
      exportValue
      importValue
      exportValueGrowth5: exportValueConstGrowth5
      importValueGrowth5: importValueConstGrowth5
      exportValueCagr5: exportValueConstCagr5
      importValueCagr5: importValueConstCagr5
      pci
      complexityEnum
    }
  }
`;

// Initialize Apollo Client
export const client = new ApolloClient({
  uri: `${baseURL}/api/graphql`,
  cache: new InMemoryCache(),
});

const toUnderscoreCase = (str: string) =>
  str.replace(/([A-Z])/g, "_$1").toLowerCase();

export const transformKeysToUnderscore = (data: any) => {
  return data.map((item: any) => {
    const transformedItem: any = {};
    for (const key in item) {
      if (item.hasOwnProperty(key)) {
        transformedItem[toUnderscoreCase(key)] = item[key];
      }
    }
    return transformedItem;
  });
};

const getFetchPromise = async () => {
  const d = await client
    .query({
      query: GET_PRODUCT_YEAR_DATA,
      variables: { productLevel: 4, productClass: "HS92" },
    })
    .then((response) => ({
      data: transformKeysToUnderscore(response.data.productYear),
    }));
  return d;
};

const {
  reducer,
  fetchDataEpic,
  fetchIfNeeded,
  getDataStatusSelector,
  getYearsStatusSelector,
} = getYearlyDataCache<
  IRootState,
  IPYDatum,
  typeof FETCH_IF_NEEDED,
  typeof FETCH_BEGIN,
  typeof FETCH_SUCCESS,
  typeof FETCH_FAIL,
  IHashInput,
  IAPIResponse
>({
  fetchIfNeededName: FETCH_IF_NEEDED,
  fetchBeginName: FETCH_BEGIN,
  fetchSuccessName: FETCH_SUCCESS,
  fetchFailName: FETCH_FAIL,
  outerHashFunction: defaultHashFunction,
  getCacheFromRootState: (rootState: IRootState) => rootState.productYear,
  getFetchPromise: getFetchPromise,
  getDataFromAPIResponse,
});

export default reducer;
export {
  fetchDataEpic,
  fetchIfNeeded,
  getDataStatusSelector,
  getYearsStatusSelector,
};
